import mergeRefs from 'merge-refs';
import { PresenceContext, usePresence } from 'motion/react';
import {
  Component,
  forwardRef,
  type HTMLAttributes,
  type MutableRefObject,
  type ReactNode,
  type RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { cn } from 'v5/platform/dom/cn';

/** The slide's length in `v4-style.animation.css` (`--animate-stack-*`). */
const SLIDE_DURATION_MS = 200;

/** Border box of a leaving panel, relative to its parent's padding box; `right`, as stacks are right-aligned. */
interface ExitBox {
  top: number;
  right: number;
  width: number;
  height: number;
}

/**
 * Measures the panel in getSnapshotBeforeUpdate, before React inserts the panel replacing it,
 * which would already have shifted it (framer-motion's PopChild does the same, from the left only).
 */
class ExitMeasure extends Component<{
  isPresent: boolean;
  elementRef: RefObject<HTMLDivElement | null>;
  exitBoxRef: MutableRefObject<ExitBox | null>;
  children: ReactNode;
}> {
  getSnapshotBeforeUpdate(prevProps: { isPresent: boolean }) {
    const element = this.props.elementRef.current;
    if (element && prevProps.isPresent && !this.props.isPresent) {
      const parent = element.offsetParent;
      // offsets, not getBoundingClientRect: an ancestor may be scaled
      this.props.exitBoxRef.current =
        parent instanceof HTMLElement
          ? {
              top: element.offsetTop,
              right:
                parent.clientWidth - element.offsetLeft - element.offsetWidth,
              width: element.offsetWidth,
              height: element.offsetHeight
            }
          : null;
    }
    return null;
  }

  // getSnapshotBeforeUpdate needs a componentDidUpdate beside it
  componentDidUpdate() {}

  render() {
    return this.props.children;
  }
}

export interface StackSlotProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  slideFrom?: 'left' | 'right';
}

/**
 * How a panel comes and goes, and nothing about how it looks: it slides in by transform and
 * opacity, and on its way out drops out of the flow at once, pinned where it was under the panels
 * that stay, so a panel replacing it takes its place on the first frame. Needs an
 * `AnimatePresence` above it and a positioned parent for the pin.
 *
 * The slide is a CSS animation, not a JS tween: it runs on the compositor, so a panel closed while
 * the main thread is busy still fades. The slot stays mounted until its exit animation has ended,
 * or, where none runs (jsdom, a hidden tab), a moment after it would have.
 */
export const StackSlot = forwardRef<HTMLDivElement, StackSlotProps>(
  (
    {
      children,
      slideFrom = 'right',
      className,
      style,
      onAnimationEnd,
      ...props
    },
    ref
  ) => {
    const [isPresent, safeToRemove] = usePresence();
    // `AnimatePresence initial={false}`: what is there when the stack mounts came without a slide
    const entersInPlace = useContext(PresenceContext)?.initial === false;
    const elementRef = useRef<HTMLDivElement>(null);
    // one ref for the element's lifetime: a new merged ref would detach and attach it every render
    const mergedRef = useMemo(() => mergeRefs(elementRef, ref), [ref]);
    const exitBoxRef = useRef<ExitBox | null>(null);
    const [exitBox, setExitBox] = useState<ExitBox | null>(null);
    // before paint, so the panel never shows sharing the row with its replacement
    useLayoutEffect(() => {
      setExitBox(isPresent ? null : exitBoxRef.current);
    }, [isPresent]);

    useEffect(() => {
      if (isPresent) return;
      const timer = setTimeout(() => safeToRemove?.(), SLIDE_DURATION_MS + 60);
      return () => clearTimeout(timer);
    }, [isPresent, safeToRemove]);

    const slide = (() => {
      if (isPresent) {
        if (entersInPlace) return undefined;
        return slideFrom === 'left'
          ? 'animate-stack-in-left'
          : 'animate-stack-in-right';
      }
      return slideFrom === 'left'
        ? 'animate-stack-out-left'
        : 'animate-stack-out-right';
    })();

    return (
      <ExitMeasure
        isPresent={isPresent}
        elementRef={elementRef}
        exitBoxRef={exitBoxRef}
      >
        <div
          ref={mergedRef}
          data-stack-exiting={isPresent ? undefined : ''}
          className={cn(
            'flex min-h-0 w-max min-w-0 flex-none shrink-0 flex-col',
            slide,
            className
          )}
          style={
            exitBox
              ? {
                  ...style,
                  position: 'absolute',
                  top: exitBox.top,
                  right: exitBox.right,
                  width: exitBox.width,
                  height: exitBox.height,
                  margin: 0,
                  // under the panels that stay: the incoming one fades in over it
                  zIndex: -1,
                  pointerEvents: 'none'
                }
              : style
          }
          onAnimationEnd={event => {
            onAnimationEnd?.(event);
            if (!isPresent && event.target === event.currentTarget) {
              safeToRemove?.();
            }
          }}
          {...props}
        >
          {children}
        </div>
      </ExitMeasure>
    );
  }
);
StackSlot.displayName = 'StackSlot';
